import { useState, type ChangeEvent } from 'react'
import { useStore } from '../store/store'
import { parseOddsCsv, parseOhlcvCsv, toCsv } from '../lib/csv'
import { stockAdapter, predictionAdapter, oddsAdapter } from '../data/adapters'
import { uid } from '../lib/id'
import { Banner, Button, Card, PageHead, Pill, TextField } from '../components/ui'

const OHLCV_SAMPLE = `Date,Open,High,Low,Close,Volume
2024-01-02,100,103,99,102.5,1200000
2024-01-03,102.5,104,101,101.2,980000`

const ODDS_SAMPLE = `event,selection,sport,american,closing
Lakers vs Suns,Lakers,NBA,-130,1.74
Lakers vs Suns,Suns,NBA,115,2.05`

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export function DataImport() {
  const store = useStore()
  const [symbol, setSymbol] = useState('TEST')
  const [name, setName] = useState('Imported Asset')
  const [ohlcvText, setOhlcvText] = useState('')
  const [oddsText, setOddsText] = useState('')
  const [msg, setMsg] = useState<{ tone: 'info' | 'warn' | 'danger'; text: string } | null>(null)

  const readFile = (e: ChangeEvent<HTMLInputElement>, target: (text: string) => void) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => target(String(reader.result ?? ''))
    reader.readAsText(file)
  }

  const importOhlcv = () => {
    const { rows, errors } = parseOhlcvCsv(ohlcvText)
    if (rows.length === 0) {
      setMsg({ tone: 'danger', text: `No candles parsed. ${errors[0] ?? 'Check your headers (Date, Open, High, Low, Close, Volume).'}` })
      return
    }
    store.upsertAssetFromCandles(symbol, name, rows)
    setMsg({ tone: errors.length ? 'warn' : 'info', text: `Imported ${rows.length} candles into ${symbol.toUpperCase()}${errors.length ? ` with ${errors.length} skipped rows.` : '.'}` })
    setOhlcvText('')
  }

  const importOdds = () => {
    const { rows, errors } = parseOddsCsv(oddsText)
    if (rows.length === 0) {
      setMsg({ tone: 'danger', text: `No odds parsed. ${errors[0] ?? 'Need event, selection and decimal/american columns.'}` })
      return
    }
    store.addOddsQuotes(rows)
    setMsg({ tone: errors.length ? 'warn' : 'info', text: `Imported ${rows.length} odds rows${errors.length ? `, skipped ${errors.length}.` : '.'}` })
    setOddsText('')
  }

  const exportJournal = () => downloadText('edgelab-journal.csv', toCsv(store.journal as unknown as Array<Record<string, unknown>>))
  const exportBankroll = () => downloadText('edgelab-bankroll.csv', toCsv(store.bankrollHistory as unknown as Array<Record<string, unknown>>))
  const exportBackup = () => downloadText(`edgelab-backup-${uid('b')}.json`, JSON.stringify(localStorage.getItem('edgelab-store-v1') ?? '{}'))

  return (
    <div className="stack">
      <PageHead title="Data Import & Export">
        Bring your own CSVs — no paid API required. EdgeLab never scrapes logged-in pages or bypasses terms of service. Import exports you already have the right to use.
      </PageHead>

      {msg && <Banner tone={msg.tone}>{msg.text}</Banner>}

      <div className="grid cols-2">
        <Card title="Import OHLCV (stocks)" subtitle="Columns: Date, Open, High, Low, Close, Volume">
          <div className="grid cols-2">
            <TextField label="Symbol" value={symbol} onChange={setSymbol} />
            <TextField label="Name" value={name} onChange={setName} />
          </div>
          <input type="file" accept=".csv,text/csv" onChange={(e) => readFile(e, setOhlcvText)} style={{ marginBottom: 8 }} />
          <textarea value={ohlcvText} onChange={(e) => setOhlcvText(e.target.value)} placeholder={OHLCV_SAMPLE} rows={6} />
          <div className="btn-row" style={{ marginTop: 8 }}>
            <Button variant="primary" onClick={importOhlcv} disabled={!ohlcvText.trim()}>Import candles</Button>
            <Button onClick={() => setOhlcvText(OHLCV_SAMPLE)}>Load sample</Button>
          </div>
        </Card>

        <Card title="Import odds (legal markets)" subtitle="Columns: event, selection, sport, american OR decimal, closing">
          <input type="file" accept=".csv,text/csv" onChange={(e) => readFile(e, setOddsText)} style={{ marginBottom: 8 }} />
          <textarea value={oddsText} onChange={(e) => setOddsText(e.target.value)} placeholder={ODDS_SAMPLE} rows={6} />
          <div className="btn-row" style={{ marginTop: 8 }}>
            <Button variant="primary" onClick={importOdds} disabled={!oddsText.trim()}>Import odds</Button>
            <Button onClick={() => setOddsText(ODDS_SAMPLE)}>Load sample</Button>
          </div>
        </Card>
      </div>

      <Card title="Export & backup">
        <div className="btn-row">
          <Button onClick={exportJournal}>Export journal CSV</Button>
          <Button onClick={exportBankroll}>Export bankroll CSV</Button>
          <Button onClick={exportBackup}>Download full JSON backup</Button>
        </div>
      </Card>

      <Card title="Data adapters" subtitle="Modular and optional. The demo runs entirely on local synthetic data — no keys, no network.">
        <div className="grid cols-3">
          {[stockAdapter, predictionAdapter, oddsAdapter].map((a) => (
            <div key={a.id} className="card tight">
              <div className="row between">
                <strong>{a.name}</strong>
                <Pill tone={a.requiresApiKey ? 'amber' : 'green'}>{a.requiresApiKey ? 'API key' : 'No key'}</Pill>
              </div>
              <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>{a.description}</p>
            </div>
          ))}
        </div>
        <Banner tone="info" >
          To add a real data source, implement the <span className="mono">StockDataAdapter</span>, <span className="mono">PredictionMarketAdapter</span> or <span className="mono">OddsAdapter</span> interface in <span className="mono">src/data/adapters</span>. Keep API keys optional and never bypass rate limits or paywalls.
        </Banner>
      </Card>
    </div>
  )
}
